import { useState, useEffect, useCallback, useRef } from "react";
import type { SyncStatusEntry, SyncRun, SyncTable } from "@shared/types";
import { apiFetch } from "../lib/apiFetch";

// ── useSyncStatus ───────────────────────────────────────────────────────────

interface SyncStatusState {
  data: SyncStatusEntry[] | null;
  isLoading: boolean;
  error: string | null;
  lastUpdatedAt: Date | null;
}

/**
 * Fetches and polls GET /api/sync/status every `intervalMs` milliseconds.
 * Returns all sources with their latest run.
 */
export function useSyncStatus(intervalMs = 30_000) {
  const [state, setState] = useState<SyncStatusState>({
    data: null,
    isLoading: true,
    error: null,
    lastUpdatedAt: null,
  });

  const fetchStatus = useCallback(async (signal?: AbortSignal) => {
    try {
      const res = await apiFetch("/api/sync/status", {
        credentials: "same-origin",
        signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as {
        sources: SyncStatusEntry[];
        count: number;
      };
      setState({
        data: json.sources,
        isLoading: false,
        error: null,
        lastUpdatedAt: new Date(),
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : "Unknown error",
      }));
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchStatus(controller.signal);

    const timer = setInterval(() => fetchStatus(controller.signal), intervalMs);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [fetchStatus, intervalMs]);

  const refresh = useCallback(() => fetchStatus(), [fetchStatus]);

  return { ...state, refresh };
}

// ── useSyncRuns ─────────────────────────────────────────────────────────────

interface SyncRunsState {
  runs: SyncRun[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Fetches GET /api/sync/runs, optionally filtered by sourceId.
 */
export function useSyncRuns(sourceId?: string, limit = 20) {
  const [state, setState] = useState<SyncRunsState>({
    runs: [],
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams({ limit: String(limit) });
    if (sourceId) params.set("source_id", sourceId);

    apiFetch(`/api/sync/runs?${params}`, {
      credentials: "same-origin",
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as { runs: SyncRun[]; count: number };
        setState({ runs: json.runs, isLoading: false, error: null });
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setState({
          runs: [],
          isLoading: false,
          error: err instanceof Error ? err.message : "Unknown error",
        });
      });

    return () => controller.abort();
  }, [sourceId, limit]);

  return state;
}

// ── useSyncTables ───────────────────────────────────────────────────────────

interface SyncTablesState {
  tables: SyncTable[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Fetches GET /api/sync/tables, optionally filtered by sourceId.
 */
export function useSyncTables(sourceId?: string) {
  const [state, setState] = useState<SyncTablesState>({
    tables: [],
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams();
    if (sourceId) params.set("source_id", sourceId);
    const qs = params.toString();

    apiFetch(`/api/sync/tables${qs ? `?${qs}` : ""}`, {
      credentials: "same-origin",
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as {
          tables: SyncTable[];
          count: number;
        };
        setState({ tables: json.tables, isLoading: false, error: null });
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setState({
          tables: [],
          isLoading: false,
          error: err instanceof Error ? err.message : "Unknown error",
        });
      });

    return () => controller.abort();
  }, [sourceId]);

  return state;
}

// ── useSyncTrigger ──────────────────────────────────────────────────────────

interface TriggerResult {
  run_id: number;
  source_id: string;
  message: string;
}

/**
 * Returns a trigger function that POSTs to /api/sync/trigger.
 * Tracks loading state and the last error per source.
 */
export function useSyncTrigger() {
  const [triggering, setTriggering] = useState<Record<string, boolean>>({});
  const [triggerError, setTriggerError] = useState<Record<string, string>>({});
  const abortRefs = useRef<Record<string, AbortController>>({});

  const trigger = useCallback(
    async (sourceId: string): Promise<TriggerResult | null> => {
      // Cancel any in-flight trigger for this source
      abortRefs.current[sourceId]?.abort();
      const controller = new AbortController();
      abortRefs.current[sourceId] = controller;

      setTriggering((prev) => ({ ...prev, [sourceId]: true }));
      setTriggerError((prev) => ({ ...prev, [sourceId]: "" }));

      try {
        const res = await apiFetch("/api/sync/trigger", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ source_id: sourceId }),
          signal: controller.signal,
        });
        if (!res.ok) {
          const errBody = (await res.json().catch(() => ({}))) as {
            error?: string;
          };
          throw new Error(errBody.error ?? `HTTP ${res.status}`);
        }
        const result = (await res.json()) as TriggerResult;
        return result;
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError")
          return null;
        const msg = err instanceof Error ? err.message : "Unknown error";
        setTriggerError((prev) => ({ ...prev, [sourceId]: msg }));
        return null;
      } finally {
        setTriggering((prev) => ({ ...prev, [sourceId]: false }));
      }
    },
    [],
  );

  return { trigger, triggering, triggerError };
}
